import { addDays, startOfWeek } from 'date-fns';

/** Convenção JavaScript: 0=domingo … 6=sábado. */
export const DEFAULT_AGENDA_VISIBLE_WEEKDAYS = [1, 2, 3, 4, 5, 6] as const;

const AGENDA_WEEKDAYS_CACHE_PREFIX = 'klinvia.agenda.visibleWeekdays:';

export function readCachedAgendaVisibleWeekdays(unitId: string): number[] | null {
  if (!unitId || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(`${AGENDA_WEEKDAYS_CACHE_PREFIX}${unitId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const normalized = normalizeAgendaVisibleWeekdays(parsed);
    return normalized;
  } catch {
    return null;
  }
}

export function writeCachedAgendaVisibleWeekdays(unitId: string, weekdays: number[]): void {
  if (!unitId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(
      `${AGENDA_WEEKDAYS_CACHE_PREFIX}${unitId}`,
      JSON.stringify(normalizeAgendaVisibleWeekdays(weekdays)),
    );
  } catch {
    /* cache opcional */
  }
}

export const AGENDA_WEEKDAY_OPTIONS: { value: number; label: string; short: string }[] = [
  { value: 0, label: 'Domingo', short: 'Dom' },
  { value: 1, label: 'Segunda-feira', short: 'Seg' },
  { value: 2, label: 'Terça-feira', short: 'Ter' },
  { value: 3, label: 'Quarta-feira', short: 'Qua' },
  { value: 4, label: 'Quinta-feira', short: 'Qui' },
  { value: 5, label: 'Sexta-feira', short: 'Sex' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
];

export function normalizeAgendaVisibleWeekdays(raw: unknown): number[] {
  let arr: number[] = [];
  if (Array.isArray(raw)) {
    arr = raw
      .map((n) => Math.floor(Number(n)))
      .filter((n) => Number.isFinite(n) && n >= 0 && n <= 6);
  }
  arr = Array.from(new Set(arr)).sort((a, b) => a - b);
  if (arr.length === 0) return [...DEFAULT_AGENDA_VISIBLE_WEEKDAYS];
  return arr;
}

/** Sem domingo na lista: semana começa na segunda; com domingo: começa no domingo (grade Dom…Sáb). */
export function agendaWeekStartsOn(visibleWeekdays: number[]): 0 | 1 {
  const normalized = normalizeAgendaVisibleWeekdays(visibleWeekdays);
  return normalized.includes(0) ? 0 : 1;
}

/** Dias visíveis na semana calendário que contém `referenceDate`, na ordem das colunas. */
export function agendaVisibleDaysInWeek(referenceDate: Date, visibleWeekdays: number[]): Date[] {
  const set = new Set(normalizeAgendaVisibleWeekdays(visibleWeekdays));
  const weekStartsOn = agendaWeekStartsOn(visibleWeekdays);
  const weekStart = startOfWeek(referenceDate, { weekStartsOn });
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const d = addDays(weekStart, i);
    if (set.has(d.getDay())) days.push(d);
  }
  return days;
}

export function agendaVisibleWeekDateBounds(
  referenceDate: Date,
  visibleWeekdays: number[],
): { start: Date; end: Date } {
  const days = agendaVisibleDaysInWeek(referenceDate, visibleWeekdays);
  const fallback = startOfWeek(referenceDate, { weekStartsOn: agendaWeekStartsOn(visibleWeekdays) });
  return {
    start: days[0] ?? fallback,
    end: days[days.length - 1] ?? fallback,
  };
}

export function stepAgendaVisibleDay(date: Date, visibleWeekdays: number[], direction: 1 | -1): Date {
  const normalized = normalizeAgendaVisibleWeekdays(visibleWeekdays);
  let d = addDays(date, direction);
  for (let i = 0; i < 370; i++) {
    if (normalized.includes(d.getDay())) return d;
    d = addDays(d, direction);
  }
  return date;
}
